import { Fragment, useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Search, Filter, ShieldCheck, ArrowRight } from "lucide-react";
import { formatIDR, fmtDate } from "@/lib/constants";
import { computeExpectedNet, computeTotalReceived, getReceiptStatus } from "@/lib/paymentReceipts";
import { computePaymentProgress } from "@/lib/paymentProgress";
import SearchableMultiSelect from "@/components/SearchableMultiSelect";
import { isTestData, isLegacyReceived, validateInvoice, incomeExclusionReason } from "@/lib/financeValidation";
import { useAuth } from "@/lib/auth";
import FinanceMetricInfoDialog from "./FinanceMetricInfoDialog";
import DuplicateReviewDialog from "./DuplicateReviewDialog";
import api from "@/lib/api";
import { useAccess } from "@/lib/access";
import PaymentDonut from "../monitoring/PaymentDonut";
import InvoiceOriginStatus from "@/components/InvoiceOriginStatus";
import PortfolioProfitCard from "@/components/PortfolioProfitCard";

/**
 * Ringkasan Tab — 149-cw-r121 (2026-08-29).
 * Rewrite untuk mendekati mockup:
 *  - Filter (tahun, bulan, project, client, status)
 *  - Daftar ringkasan per proyek (Invoice, Diterima, Sisa Piutang, Pengeluaran, Retensi, Status)
 *  - Panel detail proyek di sisi kanan
 *  - Warning validasi read-only (duplikat proyek, TEST data)
 *  - Toggle "sembunyikan data TEST_*" (aktif secara default)
 *
 * Semua perhitungan pakai formula yang sama dengan tab lain
 * (`computeExpectedNet`, `computeTotalReceived`, `getReceiptStatus`).
 */
const MONTHS = [
  { v: "all", label: "Semua Bulan" },
  ...["01","02","03","04","05","06","07","08","09","10","11","12"].map((m) => ({
    v: m,
    label: ["Januari","Februari","Maret","April","Mei","Juni","Juli","Agustus","September","Oktober","November","Desember"][Number(m)-1],
  })),
];

export default function RingkasanTab({ projects, invoiceRows, receiptRows, expenseRows, pettyCash, loading }) {
  const { user } = useAuth();
  const { canPerm } = useAccess();
  const canReviewDupe = canPerm(user, "projects", "update");
  // 2026-10-13 (R168): ringkasan perusahaan — jumlah proyek dgn biaya belum lengkap.
  const [ccIncomplete, setCcIncomplete] = useState(0);
  useEffect(() => {
    api.get("/cost-control/completeness-summary").then((r) => {
      const c = r.data?.counts || {};
      setCcIncomplete((c.belum_ditinjau || 0) + (c.belum_lengkap || 0) + (c.perlu_ditinjau_ulang || 0));
    }).catch(() => {});
  }, []);
  // 2026-06-20 (user request): tombol (i) per kartu KPI — dialog rumus (editable BOD Lv1).
  const [infoMetricKey, setInfoMetricKey] = useState(null);
  // 2026-06-20 (user request): filter multi-pilih — array kosong berarti "semua".
  const [years, setYears] = useState([]);
  const [months, setMonths] = useState([]);
  const [projectIds, setProjectIds] = useState([]);
  const [clients, setClients] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [q, setQ] = useState("");
  const [hideTest, setHideTest] = useState(true);
  // 2026-06-20 (user request): panel "Detail Proyek" dihapus — tabel full-width,
  // baris tidak lagi clickable.
  // 2026-06-20 (user request): kolom PROYEK menampilkan HANYA parent project —
  // chevron expand, badge "Main + N", dan baris anak (VO/WO) dihapus. Angka baris
  // tetap gabungan parent+anak (g.combined); panel detail tetap bisa dibuka klik baris.

  // Aggregate per project ID (STABLE, bukan by name)
  const projectAgg = useMemo(() => {
    const map = new Map();
    for (const p of projects) {
      if (p.project_type === "internal") continue;
      if (hideTest && isTestData(p)) continue;
      map.set(p.id, {
        project: p,
        invCount: 0, invoiced: 0, expectedNet: 0, received: 0,
        remaining: 0, retentionHeld: 0, retentionClaimed: 0,
        expense: 0, warnCount: 0, hasLegacy: false, invoiceRows: [],
      });
    }
    for (const r of invoiceRows) {
      const p = r.__project;
      if (!p || !map.has(p.id)) continue;
      // 2026-06-20 (user request rekonsiliasi): universe eksklusi dari SATU fungsi
      // shared (incomeExclusionReason) — sama dengan Pembayaran Masuk & rekonsiliasi.
      const excl = incomeExclusionReason(r, p, { hideTest });
      if (excl) {
        if (r.is_retention_claim) map.get(p.id).retentionClaimed += Number(r.amount) || 0;
        continue;
      }
      // 2026-10-13 (R166 Fase 2 Rekonsiliasi A.R.): invoice dibatalkan dikeluarkan
      // dari semua total (definisi sama dgn utils/ar_engine) — tetap tercatat di
      // tab Invoice & Piutang sebagai riwayat.
      if (r.is_cancelled) continue;
      const agg = map.get(p.id);
      const exp = computeExpectedNet(r);
      const rec = computeTotalReceived(r);
      agg.invCount += 1;
      // 2026-06-21: prioritas field disamakan dgn engine payment_breakdown
      // (amount → gross → invoice_amount) — Total Invoice konsisten semua halaman.
      agg.invoiced += Number(r.amount || r.gross_invoice_amount || r.invoice_amount) || 0;
      agg.expectedNet += exp;
      agg.received += rec;
      agg.remaining += Math.max(0, exp - rec);
      agg.retentionHeld += Number(r.retention_amount) || 0;
      const flags = validateInvoice(r);
      agg.warnCount += flags.length;
      if (isLegacyReceived(r)) agg.hasLegacy = true;
      agg.invoiceRows.push(r);
    }
    for (const e of expenseRows) {
      const p = e.__project;
      if (!p || !map.has(p.id)) continue;
      const agg = map.get(p.id);
      agg.expense += Number(e.amount) || 0;
    }
    return Array.from(map.values());
  }, [projects, invoiceRows, expenseRows, hideTest]);

  // 2026-06-11 (user request): grupkan per induk project. Anak VO/WO digabung
  // ke dalam grup induknya; angka baris induk = gabungan (parent + semua anak),
  // dan bisa di-breakdown via chevron. Mirror pola Summary Project.
  const groups = useMemo(() => {
    const byId = new Map(projectAgg.map((a) => [a.project.id, a]));
    const childrenOf = new Map();
    const roots = [];
    for (const a of projectAgg) {
      const pid = a.project.parent_project_id;
      if (pid && byId.has(pid)) {
        if (!childrenOf.has(pid)) childrenOf.set(pid, []);
        childrenOf.get(pid).push(a);
      } else {
        roots.push(a);
      }
    }
    const sumKeys = ["invCount", "invoiced", "expectedNet", "received", "remaining",
      "retentionHeld", "retentionClaimed", "expense", "warnCount"];
    return roots.map((root) => {
      const children = (childrenOf.get(root.project.id) || [])
        .sort((a, b) => b.remaining - a.remaining);
      const all = [root, ...children];
      const combined = { ...root };
      for (const k of sumKeys) combined[k] = all.reduce((s, x) => s + (x[k] || 0), 0);
      combined.hasLegacy = all.some((x) => x.hasLegacy);
      combined.invoiceRows = all.flatMap((x) => x.invoiceRows);
      return { root, children, combined };
    });
  }, [projectAgg]);

  // Filter options
  const yearOpts = useMemo(() => {
    const s = new Set();
    invoiceRows.forEach((r) => { if (r.invoice_date) s.add(r.invoice_date.slice(0, 4)); });
    return Array.from(s).sort().reverse();
  }, [invoiceRows]);
  const clientOpts = useMemo(() => {
    const s = new Set();
    projectAgg.forEach((r) => { if (r.project.client) s.add(r.project.client); });
    return Array.from(s).sort();
  }, [projectAgg]);
  // Status kanonis backend (payment_breakdown.invoice_status) — tidak dihitung ulang.
  const statusOf = (agg) => agg.project?.payment_breakdown?.invoice_status?.display_code || "official_none";

  const filteredGroups = useMemo(() => {
    const query = q.trim().toLowerCase();
    // Filter identitas (client/proyek/cari) lolos bila induk ATAU salah satu anak cocok.
    const matchOne = (agg) => {
      if (clients.length > 0 && !clients.includes(agg.project.client)) return false;
      if (projectIds.length > 0 && !projectIds.includes(agg.project.id)) return false;
      if (query) {
        const hay = [agg.project.project_name, agg.project.wo_number, agg.project.variation_order_name, agg.project.client]
          .filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(query)) return false;
      }
      return true;
    };
    // Filter status & periode dihitung dari angka GABUNGAN grup.
    const matchAgg = (agg) => {
      if (statuses.length > 0 && !statuses.includes(statusOf(agg))) return false;
      if (years.length > 0 || months.length > 0) {
        const anyMatch = agg.invoiceRows.some((r) => {
          if (!r.invoice_date) return false;
          if (years.length > 0 && !years.includes(r.invoice_date.slice(0, 4))) return false;
          if (months.length > 0 && !months.includes(r.invoice_date.slice(5, 7))) return false;
          return true;
        });
        if (!anyMatch) return false;
      }
      return true;
    };
    return groups
      .filter((g) => (matchOne(g.root) || g.children.some(matchOne)) && matchAgg(g.combined))
      .sort((a, b) => b.combined.remaining - a.combined.remaining);
  }, [groups, q, years, months, projectIds, clients, statuses]);

  const totals = useMemo(() => filteredGroups.reduce(
    (s, g) => ({
      invoiced: s.invoiced + g.combined.invoiced,
      expectedNet: s.expectedNet + g.combined.expectedNet,
      received: s.received + g.combined.received,
      remaining: s.remaining + g.combined.remaining,
      expense: s.expense + g.combined.expense,
      retentionHeld: s.retentionHeld + g.combined.retentionHeld,
    }),
    { invoiced: 0, expectedNet: 0, received: 0, remaining: 0, expense: 0, retentionHeld: 0 }
  ), [filteredGroups]);

  // 2026-06-20 (user request): deteksi duplikat dipindah ke backend dengan aturan
  // ketat (nama ternormalisasi + client/kontrak; client sama saja TIDAK cukup) +
  // keputusan review tersimpan. Angka peringatan = pasangan "Perlu Review" saja.
  const [dupeData, setDupeData] = useState(null);
  const [dupeOpen, setDupeOpen] = useState(false);
  const loadDupes = () => api.get(`/projects/duplicates?include_test=${!hideTest}`).then((r) => setDupeData(r.data)).catch(() => {});
  useEffect(() => { loadDupes(); /* eslint-disable-next-line */ }, [hideTest]);
  const dupePending = dupeData?.counts?.perlu_review || 0;
  const totalWarn = filteredGroups.reduce((s, g) => s + g.combined.warnCount, 0);
  const totalLegacy = filteredGroups.filter((g) => g.combined.hasLegacy).length;
  const testCount = projects.filter((p) => isTestData(p)).length;
  const activeFilterCount = [years.length, months.length, projectIds.length, clients.length, statuses.length].filter((n) => n > 0).length;
  const resetFilters = () => { setYears([]); setMonths([]); setProjectIds([]); setClients([]); setStatuses([]); };

  if (loading && projects.length === 0) {
    return <div className="p-8 text-center text-xs italic text-slate-400">Memuat ringkasan…</div>;
  }

  return (
    <div className="space-y-3" data-testid="fin-ringkasan">
      <PortfolioProfitCard testid="fin-r-portfolio" />
      <DuplicateReviewDialog open={dupeOpen} onOpenChange={setDupeOpen} data={dupeData} onReviewed={loadDupes} canReview={canReviewDupe} />
      {/* Warning banners */}
      {(dupePending > 0 || totalWarn > 0 || totalLegacy > 0 || testCount > 0) && (
        <div className="flex flex-wrap gap-2 items-center text-[11px]" data-testid="fin-warnings">
          {dupePending > 0 && (
            <button type="button" onClick={() => setDupeOpen(true)} data-testid="fin-warn-dupes"
              title="Buka daftar pasangan & review"
              className="inline-flex items-center gap-1 px-2 py-1 border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 transition-colors">
              <AlertTriangle size={11} /> {dupePending} pasangan kemungkinan proyek duplikat belum direview — klik untuk review
            </button>
          )}
          {totalWarn > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-1 border border-amber-300 bg-amber-50 text-amber-800" data-testid="fin-warn-invoice">
              <AlertTriangle size={11} /> {totalWarn} invoice bermasalah (overpay / sisa negatif / status mismatch / tanggal kosong)
            </span>
          )}
          {totalLegacy > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-1 border border-slate-300 bg-slate-50 text-slate-700" data-testid="fin-warn-legacy">
              <ShieldCheck size={11} /> {totalLegacy} proyek punya saldo penerimaan awal/legacy (tanpa riwayat pembayaran)
            </span>
          )}
          {testCount > 0 && (
            <label className="inline-flex items-center gap-1 px-2 py-1 border border-slate-300 bg-white text-slate-700 cursor-pointer" data-testid="fin-toggle-test">
              <input type="checkbox" checked={hideTest} onChange={(e) => setHideTest(e.target.checked)} className="w-3 h-3 accent-[#0B1D3A]" />
              Sembunyikan data TEST_* ({testCount})
            </label>
          )}
        </div>
      )}

      {/* Filter bar — 2026-06-20 (user request): semua dropdown multi-pilih. */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 border border-slate-200 bg-slate-50 p-3 items-end" data-testid="fin-ringkasan-filters">
        <div>
          <label className="overline text-slate-600 block mb-1">Tahun</label>
          <SearchableMultiSelect testid="fin-r-year" values={years} onChange={setYears} allLabel="Semua Tahun"
            options={yearOpts.map((y) => ({ value: y, label: y }))} widthClass="w-56" />
        </div>
        <div>
          <label className="overline text-slate-600 block mb-1">Bulan</label>
          <SearchableMultiSelect testid="fin-r-month" values={months} onChange={setMonths} allLabel="Semua Bulan"
            options={MONTHS.filter((m) => m.v !== "all").map((m) => ({ value: m.v, label: m.label }))} widthClass="w-56" />
        </div>
        <div>
          <label className="overline text-slate-600 block mb-1">Proyek</label>
          <SearchableMultiSelect testid="fin-r-project" values={projectIds} onChange={setProjectIds} allLabel="Semua Proyek"
            options={projectAgg.map((a) => ({ value: a.project.id, label: a.project.project_name || a.project.wo_number || "-" }))} widthClass="w-72" />
        </div>
        <div>
          <label className="overline text-slate-600 block mb-1">Client</label>
          <SearchableMultiSelect testid="fin-r-client" values={clients} onChange={setClients} allLabel="Semua Client"
            options={clientOpts.map((c) => ({ value: c, label: c }))} widthClass="w-64" />
        </div>
        <div>
          <label className="overline text-slate-600 block mb-1">Status</label>
          <SearchableMultiSelect testid="fin-r-status" values={statuses} onChange={setStatuses} allLabel="Semua Status"
            options={[
              { value: "official_paid", label: "Invoice resmi lunas" },
              { value: "official_partially_paid", label: "Invoice resmi dibayar sebagian" },
              { value: "official_verified_unpaid", label: "Invoice resmi belum dibayar" },
              { value: "legacy_settled", label: "Legacy tercatat lunas" },
              { value: "legacy_partially_settled", label: "Legacy tercatat sebagian" },
              { value: "legacy_unreconciled", label: "Legacy belum ada penerimaan" },
              { value: "official_draft", label: "Draft menunggu verifikasi" },
              { value: "official_none", label: "Belum ada invoice resmi terverifikasi" },
            ]} widthClass="w-56" />
        </div>
        <div className="relative">
          <label className="overline text-slate-600 block mb-1">Cari</label>
          <Search size={11} className="absolute left-2 top-[26px] text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Proyek/client…"
            data-testid="fin-r-search"
            className="w-full h-8 pl-6 pr-1.5 text-xs border border-slate-300 focus:outline-none focus:border-[#0B1D3A] bg-white" />
        </div>
        {activeFilterCount > 0 && (
          <div className="col-span-full flex justify-end">
            <button type="button" onClick={resetFilters} data-testid="fin-r-reset" className="text-[10px] uppercase tracking-wider font-semibold text-[#0B1D3A] hover:underline">Reset Filter ({activeFilterCount})</button>
          </div>
        )}
      </div>

      {/* Totals row — 4 KPI card + tombol (i) info rumus per kartu.
          2026-06-20 (user request): kartu "Kas Harus Diterima" & "Sudah Diterima"
          dihapus dari baris KPI (angkanya tetap dipakai donut Progress Penerimaan Kas). */}
      <div className="grid grid-cols-2 md:grid-cols-2 xl:grid-cols-4 gap-2" data-testid="fin-r-totals">
        <MiniStat label="Total Invoice" hint="SUM(amount) invoice non-retention, non-dibatalkan" value={formatIDR(totals.invoiced)} tone="navy" testid="tot-invoice" onInfo={() => setInfoMetricKey("total_invoice")} />
        <MiniStat label="Sisa Piutang (A.R.)" hint="Σ per-invoice (Kas Harus Diterima − Penerimaan Teralokasi), sisa > 0 saja · mengikuti filter aktif · definisi identik dgn Rekonsiliasi A.R. (utils/ar_engine)" value={formatIDR(totals.remaining)} tone="amber" testid="tot-remaining" onInfo={() => setInfoMetricKey("sisa_piutang")} />
        <MiniStat label="Pengeluaran" hint="SUM(expense_entries.amount)" value={formatIDR(totals.expense)} tone="rose" testid="tot-expense" onInfo={() => setInfoMetricKey("pengeluaran")} />
        <MiniStat label="Retensi Ditahan" hint="SUM(retention_amount) dari invoice reguler (bruto ditahan, belum dikurangi rilis) — status jatuh tempo & klaim: lihat Rekonsiliasi A.R." value={formatIDR(totals.retentionHeld)} tone="sky" testid="tot-retention" onInfo={() => setInfoMetricKey("retensi_ditahan")} />
      </div>
      {/* 2026-10-13 (R166): tautan ke engine terpadu — angka GLOBAL tanpa filter halaman ini. */}
      <div className="flex items-center justify-end -mt-1 gap-2">
        {ccIncomplete > 0 && (
          <span className="text-[10px] uppercase tracking-wider font-semibold text-amber-800 border border-amber-300 bg-amber-50 px-2 py-1" data-testid="fin-r-cc-incomplete-chip"
            title="Proyek dgn data biaya belum dikonfirmasi lengkap (belum ditinjau / belum lengkap / perlu ditinjau ulang). Konfirmasi per proyek di halaman Cost Control.">
            {ccIncomplete} proyek biaya belum lengkap
          </span>
        )}
        <Link to="/finance-report/rekonsiliasi-ar" data-testid="fin-r-open-ar-recon"
          className="text-[10px] uppercase tracking-wider font-semibold text-[#FF5A1F] hover:underline flex items-center gap-1">
          Rekonsiliasi A.R. (snapshot global) <ArrowRight size={10} />
        </Link>
        <Link to="/finance-report/rekonsiliasi-kas-staf" data-testid="fin-r-open-pc-recon"
          className="text-[10px] uppercase tracking-wider font-semibold text-[#FF5A1F] hover:underline flex items-center gap-1">
          Rekonsiliasi Kas Staf <ArrowRight size={10} />
        </Link>
        <Link to="/petty-cash/penetapan-coa" data-testid="fin-r-open-coa-queue"
          className="text-[10px] uppercase tracking-wider font-semibold text-[#FF5A1F] hover:underline flex items-center gap-1">
          Penetapan COA (Verifikasi 2) <ArrowRight size={10} />
        </Link>
      </div>

      {/* 2026-06-20 (user request): grafik donut — Progress Penerimaan Kas + Profit
          (Sudah Diterima − Pengeluaran), mengikuti filter aktif. Tiap grafik punya
          tombol (i) info formula (editable BOD Lv1). */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="bg-white border border-slate-200 p-4" data-testid="fin-r-donut-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[#0B1D3A]">Progress Pembayaran (Penerimaan Kas)</h3>
            <button type="button" data-testid="fin-r-donut-info" onClick={() => setInfoMetricKey("donut_penerimaan_kas")}
              title="Lihat formula grafik ini" aria-label="Info formula Progress Pembayaran (Penerimaan Kas)"
              className="shrink-0 inline-flex h-4 w-4 items-center justify-center border border-slate-300 text-slate-400 hover:text-[#0B1D3A] hover:border-[#0B1D3A] text-[9px] font-bold transition-colors">
              i
            </button>
          </div>
          {(() => {
            // 2026-06-20 (user request): pakai fungsi bersama computePaymentProgress —
            // Progress Penerimaan thd nilai proyek (agregat filter aktif) dengan dasar
            // = Kas Harus Diterima; sisa selalu satu denominator dgn persentase.
            const pp = computePaymentProgress({
              contractValue: totals.expectedNet, invoicedTotal: totals.expectedNet, receivedTotal: totals.received,
            });
            return (
              <PaymentDonut
                pct={pp.receiptPct}
                received={totals.received}
                remaining={pp.receiptRemaining}
                basisLabel="Kas Harus Diterima"
                over={pp.receiptOver}
                remainingLabel="Sisa Piutang (terhadap Kas Harus Diterima)"
                testid="fin-r-donut"
              />
            );
          })()}
        </div>
        <div className="bg-white border border-slate-200 p-4" data-testid="fin-r-profit-donut-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[#0B1D3A]">Selisih Invoice–Pengeluaran Tercatat</h3>
            <button type="button" data-testid="fin-r-profit-donut-info" onClick={() => setInfoMetricKey("donut_profit")}
              title="Lihat formula grafik ini" aria-label="Info formula Selisih Invoice–Pengeluaran Tercatat"
              className="shrink-0 inline-flex h-4 w-4 items-center justify-center border border-slate-300 text-slate-400 hover:text-[#0B1D3A] hover:border-[#0B1D3A] text-[9px] font-bold transition-colors">
              i
            </button>
          </div>
          <ProfitDonut invoiced={totals.invoiced} expense={totals.expense} />
        </div>
      </div>

      {/* Tabel full-width (panel Detail Proyek dihapus — user request 2026-06-20). */}
      <div className="grid grid-cols-1 gap-3">
        <div className="border border-slate-200 overflow-x-auto" data-testid="fin-r-table-wrap">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b-2 border-slate-300">
              <tr className="text-left">
                <th className="px-3 py-2 overline text-slate-700">Proyek</th>
                <th className="px-3 py-2 overline text-slate-700">Client</th>
                <th className="px-3 py-2 overline text-slate-700 text-right">Invoice</th>
                <th className="px-3 py-2 overline text-slate-700 text-right">Diterima</th>
                <th className="px-3 py-2 overline text-slate-700 text-right">Sisa Piutang</th>
                <th className="px-3 py-2 overline text-slate-700 text-right">Pengeluaran</th>
                <th className="px-3 py-2 overline text-slate-700 text-right">Retensi</th>
                <th className="px-3 py-2 overline text-slate-700">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredGroups.length === 0 && (
                <tr><td colSpan={8} className="text-center py-6 text-slate-400 italic">Tidak ada proyek sesuai filter.</td></tr>
              )}
              {filteredGroups.map((g) => {
                const agg = g.combined;
                const ist = g.root.project.payment_breakdown?.invoice_status;
                const isCombinedVO = g.root.project.vo_aggregation === "combined";
                return (
                  <Fragment key={g.root.project.id}>
                    {/* Baris INDUK — angka gabungan (parent + semua VO/WO). */}
                    <tr
                      className="border-b border-slate-100 hover:bg-slate-50"
                      data-testid={`fin-r-row-${g.root.project.id}`}>
                      <td className="px-3 py-2 text-slate-800 font-semibold max-w-[280px]" title={g.root.project.project_name}>
                        <div className="truncate">{g.root.project.project_name || g.root.project.wo_number || "-"}</div>
                        <div className="flex flex-wrap items-center gap-1 mt-0.5">
                          {isCombinedVO && <span className="text-[9px] uppercase tracking-wider font-bold px-1 py-px bg-violet-100 text-violet-800 border border-violet-300">VO Gabung</span>}
                          {agg.warnCount > 0 && <span className="text-[9px] uppercase tracking-wider font-bold text-amber-700" title="Ada warning validasi">⚠ {agg.warnCount} warning</span>}
                          {agg.hasLegacy && <span className="text-[9px] uppercase tracking-wider font-bold text-slate-600" title="Punya catatan penerimaan dari saldo historis — bukan bukti invoice resmi">📎 catatan legacy</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-slate-600 max-w-[160px] truncate" title={g.root.project.client}>{g.root.project.client || "-"}</td>
                      <td className="px-3 py-2 tabular-nums text-right font-semibold">{formatIDR(agg.invoiced)}</td>
                      <td className="px-3 py-2 tabular-nums text-right text-emerald-700 font-semibold">{formatIDR(agg.received)}</td>
                      <td className={`px-3 py-2 tabular-nums text-right font-bold ${agg.remaining > 0 ? "text-rose-700" : "text-slate-400"}`}>{formatIDR(agg.remaining)}</td>
                      <td className="px-3 py-2 tabular-nums text-right text-slate-700">{formatIDR(agg.expense)}</td>
                      <td className="px-3 py-2 tabular-nums text-right text-sky-700">{formatIDR(agg.retentionHeld)}</td>
                      <td className="px-3 py-2"><InvoiceOriginStatus status={ist} testid={`fin-r-status-${g.root.project.id}`} /></td>
                    </tr>
                  </Fragment>
                );
              })}
            </tbody>
            {filteredGroups.length > 0 && (
              <tfoot className="bg-slate-50 border-t-2 border-slate-300">
                <tr>
                  <td colSpan={2} className="px-3 py-2 overline text-[#0B1D3A] font-bold">Grand Total ({filteredGroups.length} grup proyek)</td>
                  <td className="px-3 py-2 tabular-nums text-right font-bold">{formatIDR(totals.invoiced)}</td>
                  <td className="px-3 py-2 tabular-nums text-right font-bold text-emerald-700">{formatIDR(totals.received)}</td>
                  <td className="px-3 py-2 tabular-nums text-right font-bold text-rose-700">{formatIDR(totals.remaining)}</td>
                  <td className="px-3 py-2 tabular-nums text-right font-bold">{formatIDR(totals.expense)}</td>
                  <td className="px-3 py-2 tabular-nums text-right font-bold text-sky-700">{formatIDR(totals.retentionHeld)}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <div className="border border-slate-200 bg-slate-50 p-3 text-[11px] text-slate-600 leading-relaxed" data-testid="fin-ringkasan-note">
        Pembukuan resmi dilakukan di software akuntansi terpisah. Kolom Status memakai status kanonis: &quot;Legacy tercatat lunas&quot; berasal dari saldo historis dan bukan bukti adanya invoice resmi terverifikasi; badge amber &quot;Perlu Rekonsiliasi&quot; berarti penerimaan legacy belum diklasifikasikan FAT.
      </div>

      {/* 2026-06-20 (user request): dialog info rumus kartu KPI — sama seperti tombol (i) chevron Monitoring WO. */}
      <FinanceMetricInfoDialog
        open={!!infoMetricKey}
        onOpenChange={(v) => { if (!v) setInfoMetricKey(null); }}
        metricKey={infoMetricKey}
        user={user}
      />
    </div>
  );
}

/** Donut Profit — 2026-06-20 (user request, koreksi formula): Profit = Total Invoice −
 *  Pengeluaran. Margin % = Profit ÷ Total Invoice × 100%. Segmen emerald = profit,
 *  rose = pengeluaran, dasar = Total Invoice (bukan kas diterima). */
function ProfitDonut({ invoiced, expense }) {
  const base = Number(invoiced || 0);
  const exp = Number(expense || 0);
  const profit = base - exp;
  const R = 52, C = 2 * Math.PI * R;
  const expPct = base > 0 ? Math.min(100, (exp / base) * 100) : 0;
  const expDash = (expPct / 100) * C;
  const profitDash = Math.max(0, C - expDash);
  // Margin TIDAK dihitung di sini — margin kanonis ada di Ringkasan Margin (profit_engine).
  return (
    <div className="flex items-center gap-4" data-testid="fin-r-profit-donut">
      <svg width="140" height="140" viewBox="0 0 140 140" className="shrink-0">
        <circle cx="70" cy="70" r={R} fill="none" stroke="#E2E8F0" strokeWidth="14" />
        {base > 0 && profitDash > 0 && (
          <circle cx="70" cy="70" r={R} fill="none" stroke="#059669" strokeWidth="14" strokeLinecap="round"
            strokeDasharray={`${profitDash} ${C - profitDash}`} strokeDashoffset={C / 4} transform="rotate(-90 70 70)" />
        )}
        {base > 0 && expDash > 0 && (
          <circle cx="70" cy="70" r={R} fill="none" stroke="#E11D48" strokeWidth="14"
            strokeDasharray={`${expDash} ${C - expDash}`} strokeDashoffset={C / 4 - profitDash} transform="rotate(-90 70 70)" />
        )}
        <text x="70" y="72" textAnchor="middle" className="fill-slate-500" fontSize="9">invoice vs biaya</text>
        <text x="70" y="84" textAnchor="middle" className="fill-slate-400" fontSize="7">bukan margin</text>
      </svg>
      <div className="text-xs space-y-1.5">
        <div className="flex items-center gap-2"><span className="w-2 h-2 bg-emerald-600 inline-block" /> Selisih Invoice–Pengeluaran Tercatat
          <span className={`font-semibold tabular-nums ml-1 ${profit < 0 ? "text-rose-700" : "text-slate-700"}`}>{formatIDR(profit)}</span></div>
        <div className="flex items-center gap-2"><span className="w-2 h-2 bg-rose-600 inline-block" /> Pengeluaran Tercatat
          <span className="font-semibold tabular-nums text-rose-700 ml-1">{formatIDR(exp)}</span></div>
        <div className="flex items-center gap-2"><span className="w-2 h-2 bg-slate-300 inline-block" /> Dasar: Total Invoice
          <span className="font-semibold tabular-nums text-slate-700 ml-1">{formatIDR(base)}</span></div>
        <div className="text-[10px] text-amber-700 italic" data-testid="fin-r-profit-note">
          Perbandingan invoice dan pengeluaran tercatat (arus tagihan) — BUKAN profit/margin. Margin kanonis dan status kelengkapan biaya ada di kartu Ringkasan Margin di atas.
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, tone, testid, onInfo }) {
  const tc = {
    navy: "border-l-[#0B1D3A]",
    emerald: "border-l-emerald-500",
    amber: "border-l-amber-500",
    rose: "border-l-rose-500",
    sky: "border-l-sky-500",
  }[tone] || "border-l-slate-400";
  return (
    <div className={`bg-white border border-slate-200 border-l-4 ${tc} px-3 py-2`} data-testid={testid}>
      <div className="flex items-start justify-between gap-1">
        <div className="overline text-slate-600 text-[9px]">{label}</div>
        {onInfo && (
          <button type="button" onClick={onInfo} data-testid={`${testid}-info`}
            title="Lihat rumus / penjelasan angka kartu ini" aria-label={`Info rumus ${label}`}
            className="shrink-0 inline-flex h-4 w-4 items-center justify-center border border-slate-300 text-slate-400 hover:text-[#0B1D3A] hover:border-[#0B1D3A] text-[9px] font-bold transition-colors">
            i
          </button>
        )}
      </div>
      <div className="font-display text-base font-bold text-[#0B1D3A] tabular-nums leading-tight">{value}</div>
    </div>
  );
}



